import * as React from 'react';
import * as classnames from 'classnames';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { translate, ITranslationsCollection } from 'elc-service';
import { Heading5, Body1, Body2, Heading2 } from 'elc-typography';
import { ContentSpacing } from 'elc-base-theme';
import { Row } from 'elc-grid';
import { ProductDetailsAccordion } from '../product-full/ProductDetailsAccordion';
import { diContainer, serviceNames } from '../../../service-setup/diContainer';
import { Accordion, LabelSlide } from 'elc-accordion';
import { classes } from '../../../internal/utils/Classes';
import { ProductFullViewController } from '../../controllers/ProductFullViewController';
import { ProductFullOrderableViewController } from '../../controllers/ProductFullOrderableViewController';
import { IOrderable } from '../../../exported/product-full-order/IProductFullOrderable';
import { IFieldAccordion } from '../../interfaces/IProductFull';
import { IFreeShippingAndReturnConfig } from '../../interfaces/ITranslations';

export interface IProductOverviewProps extends IOrderable {
    translations?: ITranslationsCollection;
    useOverviewAccordions?: boolean;
    useLegacyDescription?: boolean;
    productFullFieldAccordion?: IFieldAccordion;
    config?: {
        freeShippingAndReturn?: IFreeShippingAndReturnConfig;
    };
}

const StyledRow = styled(Row)`
    display: flex;
    flex-direction: column;
    margin-bottom: ${ContentSpacing.space16};
`;

const StyledHeading = styled(Heading5)`
    margin-bottom: ${ContentSpacing.space8};
`;

const StyledAccordion = styled(Accordion)`
    margin-bottom: ${ContentSpacing.space16};
`;

const StyledAdditionalContent = styled(Body2)`
    padding-top: ${ContentSpacing.space20};
`;

interface IOverviewSection {
    headingLabel: string;
    content: string;
    additionalContent?: string;
}

const OverviewSection = (props: IOverviewSection) => {
    const { headingLabel, content } = props;

    return (
        <StyledRow>
            <StyledHeading>{headingLabel}</StyledHeading>
            <Body1 dangerouslySetInnerHTML={{ __html: content }} />
        </StyledRow>
    );
};

const OverviewSectionAccordion = (props: IOverviewSection) => {
    const { headingLabel, content, additionalContent } = props;
    const productOverviewAccordionClassNames = classnames(
        'elc-product-full-accordion',
        'js-product-full-accordion'
    );
    const productOverviewAccordionAdditionalContentClassNames = classnames(
        'elc-product-full-overview-accordion-specific-content',
        'js-product-full-overview-accordion-specific-content'
    );

    return (
        <StyledAccordion className={productOverviewAccordionClassNames}>
            <LabelSlide id={`elc-${headingLabel}`} title={headingLabel}>
                <Body2 dangerouslySetInnerHTML={{ __html: content }} />
                {additionalContent && (
                    <StyledAdditionalContent
                        className={productOverviewAccordionAdditionalContentClassNames}
                    >
                        {additionalContent}
                    </StyledAdditionalContent>
                )}
            </LabelSlide>
        </StyledAccordion>
    );
};

@translate([
    'overviewLabel',
    'benefitsLabel',
    'whoItsForLabel',
    'whatItIsLabel',
    'whatItDoesLabel',
    'greatToKnowLabel',
    'keyIngredientsLabel',
    'productIngredientsAccordionLabel',
    'ingredientsDisclaimerMessage',
    'readMore',
    'readLess'
])
@observer
export class ProductOverview extends React.Component<IProductOverviewProps> {
    private productFullViewController:
        | ProductFullViewController
        | ProductFullOrderableViewController;
    private buttonLinkClass: string = classes('button-link');
    private readMoreButtonClass: string = classes('read-more-link', this.buttonLinkClass);

    constructor(props: IProductOverviewProps) {
        super(props);
        this.productFullViewController = props.isOrderable
            ? diContainer.get(serviceNames.productFullOrderableViewController)
            : diContainer.get(serviceNames.productFullViewController);
    }

    private renderDescription = () => {
        const {
            toggleDescription,
            shouldRenderReadMoreLink,
            product: { description },
            data: { isDescriptionExpanded, charLimit, productTruncatedDescription }
        } = this.productFullViewController;
        const { readMore, readLess } = this.props.translations as ITranslationsCollection;

        const readMoreText = isDescriptionExpanded ? readLess : readMore;

        const productDescription =
            !shouldRenderReadMoreLink || !charLimit || isDescriptionExpanded
                ? description
                : productTruncatedDescription;

        return (
            <>
                <Body2
                    dangerouslySetInnerHTML={{
                        __html: productDescription
                    }}
                />
                {shouldRenderReadMoreLink && (
                    <button onClick={toggleDescription} className={this.readMoreButtonClass}>
                        {readMoreText}
                    </button>
                )}
            </>
        );
    };

    public render() {
        const productOverviewClassNames = classnames('elc-product-overview', 'js-product-overview');

        const {
            overviewLabel,
            benefitsLabel,
            whoItsForLabel,
            whatItIsLabel,
            whatItDoesLabel,
            greatToKnowLabel,
            keyIngredientsLabel
        } = this.props.translations as ITranslationsCollection;

        const {
            useOverviewAccordions,
            useLegacyDescription,
            productFullFieldAccordion
        } = this.props;

        const {
            whoItsFor,
            whatItIs,
            whatItDoes,
            greatToKnow,
            keyIngredient,
            product: { benefitStatement }
        } = this.productFullViewController.data;

        // @todo To consider how brand-specific fields below can be configured to use accordions
        const isAccordion = true;
        const sectionContents = [
            [benefitsLabel, benefitStatement, !isAccordion],
            [whoItsForLabel, whoItsFor, !isAccordion],
            [whatItIsLabel, whatItIs, !isAccordion],
            [whatItDoesLabel, whatItDoes, !isAccordion],
            [greatToKnowLabel, greatToKnow, !isAccordion],
            [keyIngredientsLabel, keyIngredient, !isAccordion]
        ];

        const sectionContentsPopulatedCheck = () =>
            sectionContents.some(sectionContent => {
                return sectionContent[0] !== sectionContent[1];
            });

        const sections = sectionContents.map((sectionContent, index) => {
            const [label, content, isAccordionSection, additionalContent] = sectionContent;

            return (
                content &&
                content.trim() &&
                (isAccordionSection ? (
                    <OverviewSectionAccordion
                        headingLabel={label as string}
                        content={content as string}
                        key={`${index}-${label}`}
                        additionalContent={additionalContent as string}
                    />
                ) : (
                    <OverviewSection
                        headingLabel={label as string}
                        content={content as string}
                        key={`${index}-${label}`}
                    />
                ))
            );
        });

        const hasSections = sections.some(section => section);
        const showLegacyDescription = useLegacyDescription || !hasSections;

        const productTitleOverviewClassNames = classnames(
            'elc-product-overview-title',
            'js-product-overview-title'
        );

        const productOverviewDescriptionClassNames = classnames(
            'elc-product-overview-description',
            'js-product-overview-description'
        );

        const useAccordions = productFullFieldAccordion
            ? Object.keys(productFullFieldAccordion).length > 0
            : useOverviewAccordions;

        return (
            <div className={productOverviewClassNames}>
                {useAccordions ? (
                    <ProductDetailsAccordion
                        isOrderable={this.props.isOrderable}
                        productFullFieldAccordion={productFullFieldAccordion}
                    />
                ) : (
                    <>
                        {sectionContentsPopulatedCheck() && (
                            <StyledRow>
                                <Heading2 className={productTitleOverviewClassNames}>
                                    {overviewLabel}
                                </Heading2>
                            </StyledRow>
                        )}
                        <div className={productOverviewDescriptionClassNames}>
                            {showLegacyDescription ? this.renderDescription() : sections}
                        </div>
                    </>
                )}
            </div>
        );
    }
}
